import { SSEEventPayloadType } from '../types/sse.types';
import { ToolStatus, } from '../types/tool.types';
export const isAgentControlTool = (name) => name === 'respond_to_user' || name === 'respond_to_user_and_continue';
export function deriveToolState(sseEvents) {
    var _a;
    const toolCall = getToolCallFromEvents(sseEvents);
    const progress = getToolProgressFromEvents(sseEvents);
    const result = getToolResultFromEvents(sseEvents);
    const endEvent = getToolEndEventFromEvents(sseEvents);
    const finalEndEvent = getFinalToolEndEventFromEvents(sseEvents);
    const startEvent = getToolStartEventFromEvents(sseEvents);
    return {
        toolCall,
        progress,
        result,
        isCompleted: !!endEvent,
        isError: isToolError(endEvent),
        statusKey: (_a = endEvent === null || endEvent === void 0 ? void 0 : endEvent.payload) === null || _a === void 0 ? void 0 : _a.status_key,
        hasStarted: !!startEvent,
        startedAt: startEvent === null || startEvent === void 0 ? void 0 : startEvent.timestamp,
        finishedAt: finalEndEvent === null || finalEndEvent === void 0 ? void 0 : finalEndEvent.timestamp,
    };
}
export function getToolStartEventFromEvents(sseEvents) {
    return sseEvents.find((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_START);
}
export function getToolCallFromEvents(sseEvents) {
    const startEvent = getToolStartEventFromEvents(sseEvents);
    return startEvent === null || startEvent === void 0 ? void 0 : startEvent.payload.tool_call_data;
}
export function getToolProgressFromEvents(sseEvents) {
    const toolCall = getToolCallFromEvents(sseEvents);
    if (!toolCall) {
        return [];
    }
    const toolName = toolCall.function.name;
    const progressEvents = sseEvents
        .filter((event) => {
        var _a;
        return event.type === SSEEventPayloadType.ASSISTANT_TOOL_PROGRESS &&
            ((_a = event.payload.tool_progress_data) === null || _a === void 0 ? void 0 : _a.tool_name) === toolName;
    })
        .map((event) => event.payload.tool_progress_data)
        .filter(Boolean);
    return progressEvents;
}
export function getToolResultFromEvents(sseEvents) {
    if (process.env.NODE_ENV === 'development') {
    }
    const resultEvent = sseEvents.find((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_RESULT);
    return resultEvent === null || resultEvent === void 0 ? void 0 : resultEvent.payload.tool_result_data;
}
export function getToolEndEventFromEvents(sseEvents) {
    return sseEvents.find((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_END);
}
function getFinalToolEndEventFromEvents(sseEvents) {
    for (let index = sseEvents.length - 1; index >= 0; index -= 1) {
        if (sseEvents[index].type === SSEEventPayloadType.ASSISTANT_TOOL_USE_END) {
            return sseEvents[index];
        }
    }
    return undefined;
}
export function isToolError(endEvent) {
    if (!(endEvent === null || endEvent === void 0 ? void 0 : endEvent.payload.status_key))
        return false;
    const errorKeys = [
        'assistant.tool_execution_error',
        'assistant.error_tool_not_found',
        'assistant.error_tool_validation_failed',
        'assistant.error_tool_execution_failed',
    ];
    return errorKeys.includes(endEvent.payload.status_key);
}
export function getMessageToolCalls(message) {
    var _a;
    const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
    return sseEvents
        .filter((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_START)
        .map((event) => event.payload.tool_call_data)
        .filter(Boolean)
        .filter((toolCall) => !isAgentControlTool(toolCall.function.name));
}
export function getActiveToolMessage(message) {
    var _a;
    const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
    const toolCall = sseEvents.find((event) => {
        var _a;
        return event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_START &&
            !isAgentControlTool((_a = event.payload.tool_call_data) === null || _a === void 0 ? void 0 : _a.function.name);
    });
    return (toolCall === null || toolCall === void 0 ? void 0 : toolCall.payload.tool_call_data) || null;
}
export function getMessageChunks(message) {
    var _a;
    const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
    return sseEvents
        .filter((event) => event.type === SSEEventPayloadType.ASSISTANT_MESSAGE_CHUNK)
        .map((event) => event.payload.content || '')
        .filter(Boolean);
}
export function getMessageContent(message) {
    const chunks = getMessageChunks(message);
    return chunks.length > 0 ? chunks.join('') : message.content;
}
export function isMessageStreaming(message) {
    var _a;
    const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
    const hasChunks = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_MESSAGE_CHUNK);
    const hasComplete = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_COMPLETE);
    const hasError = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_ERROR);
    return hasChunks && !hasComplete && !hasError;
}
export function deriveToolStatus(state) {
    if (state.isError)
        return ToolStatus.FAILURE;
    if (state.isCompleted)
        return ToolStatus.SUCCESS;
    if (state.hasStarted)
        return ToolStatus.RUNNING;
    if (state.result)
        return ToolStatus.PARTIAL;
    return ToolStatus.PENDING;
}
export function useToolState(message) {
    var _a;
    const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
    return deriveToolState(sseEvents);
}
export function useMessageToolCalls(message) {
    return getMessageToolCalls(message);
}
export function useMessageContent(message) {
    return getMessageContent(message);
}
